import React, { useEffect, useRef, useState } from 'react';
import Page from 'modules/app/components/Page';
import PageContainer from 'components/shared/PageContainer';
import { ReactComponent as IconArrowRight } from 'images/arrow-right.svg';
import { ReactComponent as IconArrowLeft } from 'images/icons/arrow-left.svg';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ScrollingCarousel } from '@trendyol-js/react-carousel';
import { format } from 'date-fns';
import Tabs from 'components/shared/Tabs';
import { ReactComponent as IconSearch } from 'images/search.svg';
import { ReactComponent as IconClear } from 'images/icons/search/clear.svg';
import Input, { DateInput } from 'components/shared/Input';
import Button from 'components/shared/Button';
import useEventCalendar from 'sdk/hooks/events/useEventCalendar';
import { getFirstDay } from 'sdk/utils/dateTime';
import {
  SwiperSlide, Swiper, useSwiper, SwiperRef,
} from 'swiper/react';
import CalendarItem from '../components/CalendarItem';
import styles from './Events.module.scss';
import EventsTable from '../components/EventsTable';
import 'swiper/css/navigation';

const nameMap = new Map([
  ['Earning Reports', 'earnings'],
  ['Stock Splits', 'stockSplits'],
  ['IPOs', 'ipos'],
  ['Economic Events', 'economicEvents'],
]);

export function SwiperEvents({ currentSlide, prev, next } : {
  currentSlide: number,
  prev: boolean,
  next: boolean,
}) {
  const swiper = useSwiper();

  useEffect(() => {
    if (currentSlide !== swiper.activeIndex) {
      swiper.slideTo(currentSlide, 500)
    }
  }, [currentSlide])

  useEffect(() => {
    if (next) {
      swiper.slideNext()
    }
  }, [next])

  useEffect(() => {
    if (prev) {
      swiper.slidePrev()
    }
  }, [prev])

  return null
}

function Events({
  activeLink,
} : {
  activeLink: string,
}) {
  const swiperRef = useRef<SwiperRef>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { search: searchQuery } = useLocation()
  const [dateChanging, setDateChanging] = useState(false);
  const [search, setSearch] = useState('')
  const [openSearch, setOpenSearch] = useState(false)
  const [currentCount, setCurrentCount] = useState(0);

  const {
    eventsList, eventListLoading, currentDate, setCurrentDate, fromDate, setFromDate,
  } = useEventCalendar(search, 'EEE, MMM d')

  const handleSearchChange = (e: React.FormEvent<HTMLInputElement>) => {
    setSearch((e.target as HTMLInputElement).value)
  }

  const tabs = [
    { id: 0, title: 'All Events', link: '/events' },
    { id: 1, title: 'Earning Reports', link: '/events/earnings' },
    { id: 2, title: 'Stock Splits', link: '/events/stock-splits' },
    { id: 3, title: 'IPOs', link: '/events/ipos' },
    { id: 4, title: 'Economic Events', link: '/events/economic-events' },
  ]

  const activeTab = tabs.filter((item) => item.link === activeLink)[0];

  useEffect(() => {
    setCurrentDate(new Date(searchParams.get('date') || new Date()))
  }, [])

  useEffect(() => {
    if (!dateChanging && searchParams.get('date') && searchParams.get('date') !== format(currentDate, 'yyyy-MM-dd')) {
      setCurrentDate(new Date(searchParams.get('date') || ''))
    }
  }, [searchQuery])

  useEffect(() => {
    if (currentDate && format(getFirstDay(currentDate), 'yyyy-MM-dd') !== format(fromDate, 'yyyy-MM-dd')) {
      setFromDate(getFirstDay(currentDate))
    }
    if (currentDate && searchParams.get('date') !== format(currentDate, 'yyyy-MM-dd')) {
      setDateChanging(true);
      setTimeout(() => {
        setDateChanging(false);
      }, 1000)
      setSearchParams([
        ['date', format(currentDate, 'yyyy-MM-dd')],
      ])
    }
  }, [currentDate])

  useEffect(() => {
    if (eventsList) {
      if (activeTab.title !== 'All Events') {
        const curEvent = eventsList.filter((item) => item.date === format(currentDate, 'yyyy-MM-dd'))[0];
        const eventName = nameMap.get(activeTab.title) || '';
        setCurrentCount(Number(curEvent?.[eventName]))
      } else {
        setCurrentCount(0)
      }
    }
  }, [activeTab, currentDate, eventsList])

  const currentIndex = eventsList.findIndex((item) => item.date === format(currentDate, 'yyyy-MM-dd'))

  const nextSlide = (e: React.MouseEvent) => {
    e.preventDefault()
    swiperRef.current?.swiper.slideTo(currentIndex + 6)
  }

  const prevSlide = (e: React.MouseEvent) => {
    e.preventDefault()
    swiperRef.current?.swiper.slideTo(currentIndex - 6)
  }

  return (
    <PageContainer className={styles.container}>
      <div className={styles.headerRow}>
        <div className={styles.h} onClick={() => navigate('/home')}>
          <h1 className={styles.title}>
            <IconArrowRight />
            Event Calendar
          </h1>
        </div>
      </div>
      <div className={styles.calendarBlock}>
        <div className={styles.calendarHeader}>
          {!openSearch ? (
            <>
              <Tabs
                className={styles.tabs}
                activeLink={activeLink}
                items={tabs}
                inline
                links
              />
              <div className={styles.calendarHeaderRight}>
                {activeTab.title !== 'All Events' && (
                  <Button
                    className={styles.searchBtn}
                    onClick={() => setOpenSearch(true)}
                    iconBtn
                  >
                    <IconSearch />
                  </Button>
                )}
                <DateInput
                  pickerClassName={styles.dayInput}
                  value={currentDate}
                  onChange={(value: Date | null) => {
                    if (value) {
                      setCurrentDate(value)
                    }
                  }}
                  dateFormat="MMM, dd"
                  withArrow
                  allDates
                  fromDate={fromDate}
                />
              </div>
            </>
          ) : (
            <Input
              icon={<IconSearch className={styles.prefix} />}
              suffix={(
                <IconClear
                  className={styles.suffix}
                  onClick={() => {
                    setOpenSearch(false)
                    setSearch('')
                  }}
                />
              )}
              className={styles.searchInput}
              value={search}
              onChange={handleSearchChange}
              placeholder="Search on Beautify"
            />
          )}
        </div>
        {eventListLoading ? (
          <ScrollingCarousel>
            {Array.from(Array(7).keys())?.map((item) => (
              <CalendarItem key={item} skeleton />
            ))}
          </ScrollingCarousel>
        ) : (
          <div className={styles.itemsContainer}>
            <Swiper
              ref={swiperRef}
              className={styles.items}
              slidesPerView="auto"
              initialSlide={currentIndex}
              freeMode
              wrapperClass={styles.itemsWrapper}
            >
              {eventsList?.map((item) => (
                <SwiperSlide key={item.date} className={styles.itemSlide}>
                  <CalendarItem
                    data={item}
                    date={format(currentDate, 'yyyy-MM-dd')}
                    activeLink={activeLink}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
            <div className={styles.sliderPrev} onClick={prevSlide}><IconArrowLeft /></div>
            <div className={styles.sliderNext} onClick={nextSlide}><IconArrowRight /></div>
          </div>
        )}
        {activeTab.title !== 'All Events' && (
          <EventsTable
            name={activeTab.title}
            date={currentDate}
            search={search}
            count={currentCount}
          />
        )}
        {activeTab.title === 'All Events'
        && tabs.filter((item) => item.title !== 'All Events').map((item) => {
          const curEvent = eventsList.filter((item2) => item2.date === format(currentDate, 'yyyy-MM-dd'))[0];
          const eventName = nameMap.get(item.title) || '';
          if (Number(curEvent?.[eventName])) {
            return (
              <EventsTable
                name={item.title}
                date={currentDate}
                count={3}
                link={`${item.link}?date=${format(currentDate, 'yyyy-MM-dd')}`}
                short
              />
            )
          }
          return null
        })}
      </div>
    </PageContainer>
  );
}

function EventsPage({
  activeLink,
} : {
  activeLink: string,
}) {
  return (
    <Page activeLink="/">
      <Events activeLink={activeLink} />
    </Page>
  );
}

export default EventsPage;
